/**
 * Cart Artwork Upload Handler
 * Modern JavaScript module for handling print artwork uploads in cart
 * No jQuery dependency
 */

const ARTWORK_PRODUCT_ID = 43006247927964;

class ArtworkUploadHandler {
  constructor() {
    this.uploading = new Set();
    this.init();
  }

  init() {
    // Listen for cart updates to re-bind event handlers
    document.addEventListener('cart:updated', () => this.bindHandlers());
    document.addEventListener('cart:refresh-completed', () => this.bindHandlers());

    // Initial bind
    this.bindHandlers();
  }

  bindHandlers() {
    this.bindUploadHandlers();
    this.bindRemoveHandlers();
  }

  bindUploadHandlers() {
    const uploadInputs = document.querySelectorAll('.artwork-upload-input');

    uploadInputs.forEach(input => {
      // Remove existing listeners to prevent duplicates
      const newInput = input.cloneNode(true);
      input.parentNode.replaceChild(newInput, input);

      // Add change event listener
      newInput.addEventListener('change', (e) => this.handleUpload(e));
    });
  }

  bindRemoveHandlers() {
    const removeButtons = document.querySelectorAll('.artwork-remove-button');
    removeButtons.forEach(button => {
      // Prevent double binding
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', (e) => this.handleRemove(e));
    });
  }

  async handleRemove(event) {
    const button = event.currentTarget;
    const artworkKey = button.dataset.artworkKey;

    if (!artworkKey) return;

    if (!confirm('Are you sure you want to remove this artwork?')) return;

    button.disabled = true;
    button.classList.add('btn--loading');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: artworkKey,
          quantity: 0
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove artwork');
      }

      // Refresh cart display
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { open: false }
      }));

    } catch (error) {
      console.error('Artwork removal error:', error);
      button.disabled = false;
      button.classList.remove('btn--loading');
      alert('Failed to remove artwork. Please try again.');
    }
  }

  async handleUpload(event) {
    const input = event.target;
    const file = input.files[0];

    if (!file) return;

    const packageId = input.dataset.for;
    const printKey = input.dataset.printKey;
    const location = input.dataset.location;

    if (!packageId) {
      console.error('No package ID found for upload');
      return;
    }

    if (!printKey) {
      console.error('No print key found for upload');
      return;
    }

    // Prevent duplicate uploads for this specific print
    const uploadKey = `${packageId}-${printKey}`;
    if (this.uploading.has(uploadKey)) {
      return;
    }

    this.uploading.add(uploadKey);
    this.showUploadingState(input);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('id', ARTWORK_PRODUCT_ID);
      formData.append('quantity', 1);
      formData.append('properties[_package]', packageId);
      formData.append('properties[_print_key]', printKey);
      if (location) {
        formData.append('properties[For]', location);
      }
      formData.append('properties[_image]', file);
      formData.append('properties[_image_name]', file.name);

      // Upload to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      // Refresh cart display
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { open: false } // Don't auto-open, just refresh
      }));

      // Show success state
      this.showSuccessState(input, file.name);

    } catch (error) {
      console.error('Artwork upload error:', error);
      this.showErrorState(input);
      alert('Failed to upload artwork. Please try again.');
    } finally {
      this.uploading.delete(uploadKey);
    }
  }

  showUploadingState(input) {
    const container = input.closest('.artwork-upload-container');
    if (container) {
      container.classList.add('uploading');
      const button = container.querySelector('.artwork-upload-button');
      if (button) {
        button.disabled = true;
        button.textContent = 'Uploading...';
      }
    }
  }

  showSuccessState(input, fileName) {
    const container = input.closest('.artwork-upload-container');
    if (container) {
      container.classList.remove('uploading');
      container.classList.add('uploaded');
      const button = container.querySelector('.artwork-upload-button');
      if (button) {
        button.textContent = 'Uploaded';
        button.disabled = false;
      }
    }
  }

  showErrorState(input) {
    const container = input.closest('.artwork-upload-container');
    if (container) {
      container.classList.remove('uploading');
      const button = container.querySelector('.artwork-upload-button');
      if (button) {
        button.textContent = 'Upload Artwork';
        button.disabled = false;
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ArtworkUploadHandler();
  });
} else {
  new ArtworkUploadHandler();
}
